import React from 'react';
import { observer } from 'mobx-react';
import { Trans, useDisplayStringValue, useTranslation } from '@gamiphy/react-i18n';
import {
  Box, Button, Divider, Grid,
} from '@mui/material';
import { Tier, TierColors, TierConfigType } from '@gamiphy/bl/level';
import Color from 'color';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';
import { UnavowedPopup } from '@gamiphy/loyalty-station-desktop/components/popups/unavowed';
import BenefitsList from './BenefitsList';
import { useTierBenefits } from '../hooks';

const TierBenefits: React.FC<{
  tier: Tier,
}> = observer(({
  tier,
}) => {
  const { t } = useTranslation();
  const [popupOpen, setPopupOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [showButton, setShowButton] = React.useState<boolean>(false);
  const boxRef = React.useRef<HTMLDivElement | null>(null);

  const benefits = useTierBenefits({
    tier,
    openUnavowedPopup: () => setPopupOpen(true),
  });
  const btnText = React.useMemo(() => {
    switch (expanded) {
      case false:
        return (
          <Trans
            i18nKey="tier.benefits.more"
            defaults="View more"
          />
        );
      default:
        return (
          <Trans
            i18nKey="tier.benefits.less"
            defaults="View less"
          />
        );
    }
  }, [expanded]);

  const tierColor = React.useMemo<string>(() => tier?.color ?? TierColors.bronze, [tier?.color]);

  React.useEffect(() => {
    if (boxRef.current && (boxRef.current?.offsetHeight < boxRef.current?.scrollHeight)) {
      setShowButton(true);
    }
  }, [boxRef]);
  return (
    <Grid
      sx={{
        borderRadius: '8px',
        overflowX: 'hidden',
        wordBreak: 'break-word',
      }}
    >
      <Grid
        item
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{
          backgroundColor: tierColor,
          p: '8px',
          color: Color(tierColor)
            .isDark() ? '#fff' : '#000',
        }}
      >
        <Grid
          item
          xs={6}
          sx={{
            textAlign: 'left',
            fontSize: 14,
          }}
        >
          {useDisplayStringValue(tier?.name)}
        </Grid>
        {(LevelStore.tierConfig?.type === TierConfigType.exp || LevelStore.tierConfig?.type === TierConfigType.gainedPoints) && (
          <Grid
            item
            xs={6}
            sx={{
              textAlign: 'right',
              textTransform: 'capitalize',
            }}
          >
            <Trans
              i18nKey="levels.needed.amount"
              defaults="{{neededAmount}} {{label}}"
              values={{
                neededAmount: tier?.neededAmount,
                label: LevelStore.tierConfig?.type === TierConfigType.exp ? (t('shard.exp', 'Exp')) : (t('shared.points', 'points')),
              }}
            />
          </Grid>
        )}
      </Grid>
      <Grid
        item
        container
        direction="column"
        sx={{
          borderWidth: '0px 1px 1px 1px',
          borderStyle: 'solid',
          borderColor: `${tierColor}`,
          borderRadius: '0 0 8px 8px',
        }}
      >
        <Grid item>
          <Box
            ref={boxRef}
            sx={{
              height: 'auto',
              maxHeight: expanded ? 'auto' : 200,
              overflowY: 'hidden',
            }}
          >
            {
              benefits.length > 0
                ? (
                  <BenefitsList benefits={benefits} />
                )
                : (
                  <Box sx={{
                    p: '8px',
                    fontSize: 13,
                  }}
                  >
                    <Trans
                      i18nKey="tier.benefits.unavailable"
                      defaults="No benefits available"
                      values={{}}
                    />
                  </Box>
                )
            }
          </Box>
        </Grid>
        {showButton && (
          <Grid item>
            <Divider sx={{ borderBottomWidth: 2 }} />
            <Grid item>
              <Button
                sx={{
                  backgroundColor: '#fff',
                  color: 'primary.main',
                  borderRadius: '8px',
                  height: '2rem',
                }}
                fullWidth
                onClick={() => setExpanded((prevState) => !prevState)}
              >
                {btnText}
              </Button>
            </Grid>
          </Grid>
        )}
      </Grid>
      <UnavowedPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        title={t('unavowedPopup.rewardTitle', 'Sign up now to redeem your coupon for exclusive discounts!')}
      />
    </Grid>
  );
});

export default TierBenefits;
